import { useLocalStorage } from "@vueuse/core";
import { v4 as uuidv4 } from "uuid";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useThemeContext } from "@/core/composables";
import {
  CONFIG_PRODUCTS_TO_COMPARE_LOCAL_STORAGE,
  PRODUCT_COMPARE_LIST_IDS_LOCAL_STORAGE,
  DEFAULT_NOTIFICATION_DURATION,
  LOCAL_PRODUCT_CONFIGURATIONS_LOCAL_STORAGE,
} from "@/core/constants";
import { truncate } from "@/core/utilities";
import { CONFIGURABLE_SECTION_TYPES } from "@/shared/catalog/constants/configurableProducts";
import { compareConfigurationInputs } from "@/shared/catalog/utilities/configurations";
import { useNotifications } from "@/shared/notification";
import type { IConfigurationProperty, IConfigProductToCompare } from "../types";
import type { Product, ConfigurationSectionInput } from "@/core/api/graphql/types";
import type { LocalConfigurationType } from "@/shared/catalog/types";

const NOTIFICATIONS_GROUP = "compare-products";
const DEFAULT_MAX_PRODUCTS = 5;
const NAME_MAX_LENGTH = 60;

const productsIds = useLocalStorage<string[]>(PRODUCT_COMPARE_LIST_IDS_LOCAL_STORAGE, []);
const configProductsToCompare = useLocalStorage<IConfigProductToCompare[]>(
  CONFIG_PRODUCTS_TO_COMPARE_LOCAL_STORAGE,
  [],
);
const localProductConfigurations = useLocalStorage<LocalConfigurationType[]>(
  LOCAL_PRODUCT_CONFIGURATIONS_LOCAL_STORAGE,
  [],
);

function addRegularProductToCompare(product: Product) {
  if (productsIds.value.includes(product.id)) {
    return;
  }
  productsIds.value.push(product.id);
}

function removeRegularProductFromCompare(product: Product) {
  const index = productsIds.value.indexOf(product.id);
  if (index !== -1) {
    productsIds.value.splice(index, 1);
  }
}

function removeConfiguredProductFromCompare(product: Product, configuration?: ConfigurationSectionInput[]) {
  const index = findMatchingConfigProductIndex(product, configuration);
  const configurationLocalId = localProductConfigurations.value[index].localId;

  if (index !== -1) {
    configProductsToCompare.value.splice(index, 1);
  }

  const configurationIndex = localProductConfigurations.value.findIndex(
    (config) => config.localId === configurationLocalId,
  );
  if (configurationIndex !== -1) {
    localProductConfigurations.value.splice(configurationIndex, 1);
  }
}

function addConfiguredProductToCompare(
  product: Product,
  configurationSectionsInput?: ConfigurationSectionInput[],
  properties?: IConfigurationProperty[],
) {
  if (isInCompareList(product, configurationSectionsInput)) {
    return;
  }

  const localId = uuidv4();
  const configurationSectionsInputWithoutFiles = configurationSectionsInput?.filter(
    (section) => section.type !== CONFIGURABLE_SECTION_TYPES.file,
  );

  configProductsToCompare.value.push({
    localId,
    productId: product.id,
    configurationSectionInput: configurationSectionsInputWithoutFiles,
    properties: properties ?? [],
  });

  if (configurationSectionsInputWithoutFiles) {
    localProductConfigurations.value.push({
      localId,
      configuration: configurationSectionsInputWithoutFiles.map((section) => ({
        ...section,
        ...section.option,
        id: section.sectionId,
      })),
    });
  }
}

function isInCompareList(product: Product, configuration?: ConfigurationSectionInput[]) {
  if (product.isConfigurable && configuration?.length) {
    return findMatchingConfigProductIndex(product, configuration) !== -1;
  }
  return productsIds.value.includes(product.id);
}

function findMatchingConfigProductIndex(product: Product, configuration?: ConfigurationSectionInput[]) {
  if (!configuration) {
    return -1;
  }

  return configProductsToCompare.value.findIndex((configProduct) => {
    if (configProduct.productId !== product.id) {
      return false;
    }

    if (!configProduct.configurationSectionInput?.length) {
      return false;
    }

    return configuration.every((section) => {
      const matched = configProduct.configurationSectionInput?.find((s) => s.sectionId === section.sectionId);
      return matched ? compareConfigurationInputs(section, matched) : false;
    });
  });
}

export function useCompareProducts() {
  const { themeContext } = useThemeContext();
  const { t } = useI18n();

  const notifications = useNotifications();
  const productsLimit = themeContext.value?.settings?.product_compare_limit || DEFAULT_MAX_PRODUCTS;

  function addToCompareList(
    product: Product,
    configurationSections?: ConfigurationSectionInput[],
    properties?: IConfigurationProperty[],
  ) {
    if (productsIds.value.length + configProductsToCompare.value.length >= productsLimit) {
      notifications.warning({
        duration: DEFAULT_NOTIFICATION_DURATION,
        group: NOTIFICATIONS_GROUP,
        singleInGroup: true,
        text: t("custom.compare.notifications.limit", productsLimit),
      });

      return;
    }

    if (product.isConfigurable && configurationSections?.length) {
      addConfiguredProductToCompare(product, configurationSections, properties);
    } else {
      addRegularProductToCompare(product);
    }

    const productName = truncate(product.name, NAME_MAX_LENGTH);
    const itemsLeft = productsLimit - productsIds.value.length;

    const notificationMessage = t("custom.compare.notifications.product_added_to_compare_list", {
      productName,
      itemsLeft,
    });

    notifications.success({
      duration: DEFAULT_NOTIFICATION_DURATION,
      group: NOTIFICATIONS_GROUP,
      singleInGroup: true,
      // Innovadis custom
      html: notificationMessage,
      button: {
        text: "Compare",
        to: { path: "/compare" },
        clickHandler() {
          notifications.clear(NOTIFICATIONS_GROUP);
        },
      },
    });
  }

  function removeFromCompareList(product: Product, configurationSections?: ConfigurationSectionInput[]) {
    if (product.isConfigurable && configurationSections?.length) {
      removeConfiguredProductFromCompare(product, configurationSections);
    } else {
      removeRegularProductFromCompare(product);
    }

    const productName = truncate(product.name, NAME_MAX_LENGTH);

    const notificationMessage = t("custom.compare.notifications.product_removed_from_compare_list", {
      productName,
    });

    notifications.warning({
      duration: DEFAULT_NOTIFICATION_DURATION,
      group: NOTIFICATIONS_GROUP,
      singleInGroup: true,
      // Innovadis custom
      html: notificationMessage,
    });
  }

  function clearCompareList() {
    productsIds.value = [];
    configProductsToCompare.value = [];
  }

  return {
    addToCompareList,
    removeFromCompareList,
    clearCompareList,
    isInCompareList,

    productsLimit,
    productsIds: computed(() => productsIds.value.slice(0, productsLimit - configProductsToCompare.value.length)),
    configProductsToCompare: computed(() =>
      configProductsToCompare.value.slice(0, productsLimit - productsIds.value.length),
    ),
  };
}
